import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { MINUTE_IN_MILLISECONDS } from "shared/constants";
import { isSocialPlatformTokenValid } from "shared/helpers/social";
import OrganisationSocialIntegration from "shared/resources/OrganisationSocialIntegration";
import features from "shared/services/features";
import { useUserStore } from "shared/stores/user";
import { type NonNullableKey } from "shared/types";

type IntegrationWithId = NonNullableKey<OrganisationSocialIntegration, "id">;

const VISIBLE_TO_ALL_TEAMS_ID = 0;
export const REQUIRED_FEATURES = {
  hasTeamWorkspaces: "has_team_workspaces",
} as const;

export const useSocialIntegrationsStore = defineStore(
  "socialIntegrations",
  () => {
    const integrations = ref<OrganisationSocialIntegration[]>([]);
    const refreshedAt = ref(Date.now());

    const engageIntegrations = computed(() =>
      integrations.value.filter(
        (integration) => integration.isSocialEngage === true
      )
    );

    const engageTeamIntegrations = computed(() =>
      teamIntegrations({ engageOnly: true })
    );

    function teamIntegrations({ engageOnly = false }) {
      const socialIntegrations = engageOnly
        ? engageIntegrations.value
        : integrations.value;

      if (!useUserStore().organisation.organisation_teams) {
        return socialIntegrations;
      }

      const userTeamId = features.has(REQUIRED_FEATURES.hasTeamWorkspaces)
        ? useUserStore().preferences.selected_team_social
        : useUserStore().currentUser.primary_organisation_team_id;

      if (!userTeamId) return [];

      return socialIntegrations.filter(
        (integration) =>
          integration.organisationTeamIds === null ||
          [userTeamId, VISIBLE_TO_ALL_TEAMS_ID].some((teamId) =>
            integration.organisationTeamIds.includes(teamId)
          )
      );
    }

    const teamIntegrationsRecord = computed(() =>
      Object.fromEntries(
        teamIntegrations({}).map((integration) => [integration.id, integration])
      )
    );

    const expiredIntegrationsRecord = computed(() =>
      Object.fromEntries(
        integrations.value
          .filter((integration) => !isSocialPlatformTokenValid(integration))
          .map((integration) => [integration.id, integration])
      )
    );

    const integrationsBySocialPlatform = computed(() => {
      const grouped: Partial<
        Record<
          OrganisationSocialIntegration["socialPlatform"],
          Record<string, IntegrationWithId>
        >
      > = {};

      teamIntegrations({}).forEach((integration) => {
        const platform = integration.socialPlatform;

        if (!grouped[platform]) {
          grouped[platform] = {};
        }

        grouped[platform]![integration.id as string] =
          integration as IntegrationWithId;
      });

      return grouped;
    });

    function setIntegrations(newIntegrations: OrganisationSocialIntegration[]) {
      integrations.value = newIntegrations.map((integration) =>
        integration.dup()
      );

      refreshedAt.value = Date.now();
    }

    function loadIntegrations({ reload }: { reload?: boolean } = {}) {
      if (
        integrations.value.length &&
        !reload &&
        refreshedAt.value > Date.now() - 5 * MINUTE_IN_MILLISECONDS
      ) {
        return Promise.resolve();
      }

      return OrganisationSocialIntegration.where({
        archivedAt: { eq: "null" },
        isOutreachAccount: { eq: false },
      })
        .selectExtra(["scheduledPosts", "draftPosts"])
        .per(999)
        .all()
        .then((result) => {
          setIntegrations(result.data);
        });
    }

    function filterIntegrationsByType<T extends OrganisationSocialIntegration>(
      type: T["socialPlatform"]
    ): T[] {
      return integrations.value.filter(
        (integration): integration is T => integration.socialPlatform === type
      );
    }

    return {
      expiredIntegrationsRecord,
      integrations,
      integrationsBySocialPlatform,
      engageIntegrations,
      refreshedAt,
      teamIntegrations,
      teamIntegrationsRecord,
      engageTeamIntegrations,

      loadIntegrations,
      setIntegrations,
      filterIntegrationsByType,
    };
  }
);
